import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Play, RotateCcw, Rocket, Loader2 } from 'lucide-react';
import { format, subDays } from 'date-fns';

interface StrategyData {
  id: string;
  name: string;
  description: string;
  accuracy: string;
  live_users: number;
  category: string;
  logic: any;
  risk_level: 'Low' | 'Medium' | 'High';
  timeframe: string;
  is_predefined: boolean;
}

interface StrategyTestModalProps {
  strategy: StrategyData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface BacktestResult {
  totalTrades: number;
  winRate: number;
  totalReturn: number;
  maxDrawdown: number;
  sharpeRatio: number;
  startBalance: number;
  endBalance: number;
}

interface BacktestTrade {
  pnl: number;
  trade_timestamp: string;
  side: string;
  price: number;
}

const SYMBOLS = [
  { value: 'EUR/USD', label: 'EUR/USD' },
  { value: 'GBP/USD', label: 'GBP/USD' },
  { value: 'USD/JPY', label: 'USD/JPY' },
  { value: 'AUD/USD', label: 'AUD/USD' },
  { value: 'BTC/USD', label: 'BTC/USD' },
  { value: 'ETH/USD', label: 'ETH/USD' },
  { value: 'XAU/USD', label: 'XAU/USD (Gold)' },
  { value: 'XAG/USD', label: 'XAG/USD (Silver)' },
];

const today = new Date();
const defaultStart = format(subDays(today, 180), 'yyyy-MM-dd');
const defaultEnd = format(today, 'yyyy-MM-dd');

function EquityCurve({ trades, startBalance }: { trades: BacktestTrade[]; startBalance: number }) {
  const { path, points, isProfit, min, max } = useMemo(() => {
    if (!trades.length) return { path: '', points: [], isProfit: true, min: 0, max: 0 };

    const sorted = [...trades].sort(
      (a, b) => new Date(a.trade_timestamp).getTime() - new Date(b.trade_timestamp).getTime()
    );

    let balance = startBalance;
    const pts = [{ x: 0, value: balance }];
    sorted.forEach((t, i) => {
      balance += t.pnl;
      pts.push({ x: i + 1, value: balance });
    });

    const values = pts.map((p) => p.value);
    const minV = Math.min(...values);
    const maxV = Math.max(...values);
    const range = maxV - minV || 1;
    const w = 400;
    const h = 120;
    const padY = 8;

    const mapped = pts.map((p) => ({
      x: (p.x / (pts.length - 1)) * w,
      y: h - padY - ((p.value - minV) / range) * (h - padY * 2),
    }));

    let d = `M ${mapped[0].x} ${mapped[0].y}`;
    for (let i = 1; i < mapped.length; i++) {
      const prev = mapped[i - 1];
      const curr = mapped[i];
      const cpX = (prev.x + curr.x) / 2;
      d += ` Q ${cpX} ${prev.y} ${cpX} ${(prev.y + curr.y) / 2}`;
    }
    const last = mapped[mapped.length - 1];
    d += ` L ${last.x} ${last.y}`;

    return {
      path: d,
      points: mapped,
      isProfit: balance >= startBalance,
      min: minV,
      max: maxV,
    };
  }, [trades, startBalance]);

  if (!trades.length) return null;

  const strokeColor = isProfit ? 'hsl(var(--chart-2))' : 'hsl(var(--destructive))';

  return (
    <div className="rounded-lg border border-border/20 bg-card/30 p-4">
      <p className="text-xs text-muted-foreground mb-2 font-medium">Equity Curve</p>
      <svg viewBox="0 0 400 120" className="w-full h-auto" preserveAspectRatio="none">
        <defs>
          <linearGradient id="eq-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.15" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        {path && (
          <>
            <path d={`${path} L 400 120 L 0 120 Z`} fill="url(#eq-grad)" />
            <path d={path} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
          </>
        )}
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span>${min.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
        <span>${max.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
      </div>
    </div>
  );
}

export const StrategyTestModal = ({ strategy, open, onOpenChange }: StrategyTestModalProps) => {
  const [testParams, setTestParams] = useState({
    startDate: defaultStart,
    endDate: defaultEnd,
    initialBalance: 10000,
    symbol: 'EUR/USD',
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<BacktestResult | null>(null);
  const [trades, setTrades] = useState<BacktestTrade[]>([]);
  const { toast } = useToast();

  if (!strategy) return null;

  const sl = strategy.logic?.sl ?? 2;
  const tp = strategy.logic?.tp ?? 5;

  const handleRunTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTrades([]);

    try {
      const { data, error } = await supabase.functions.invoke('run-backtest', {
        body: {
          strategy_name: strategy.name.toLowerCase().replace(/\s+/g, '-'),
          parameters: { symbol: testParams.symbol, position_size: 0.1 },
          symbol: testParams.symbol,
          start_date: testParams.startDate,
          end_date: testParams.endDate,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const netProfit = data.netProfit ?? 0;
      const totalReturn =
        testParams.initialBalance > 0 ? (netProfit / testParams.initialBalance) * 100 : 0;
      const maxDd =
        testParams.initialBalance > 0
          ? (Math.abs(data.maxDrawdown ?? 0) / testParams.initialBalance) * 100
          : 0;

      setTestResult({
        totalTrades: data.totalTrades ?? 0,
        winRate: data.winRate ?? 0,
        totalReturn,
        maxDrawdown: maxDd,
        sharpeRatio: data.sharpeRatio ?? 0,
        startBalance: testParams.initialBalance,
        endBalance: testParams.initialBalance + netProfit,
      });

      if (Array.isArray(data.trades)) {
        setTrades(data.trades);
      }

      toast({
        title: 'Backtest Complete',
        description: `Strategy tested on ${testParams.symbol}`,
      });
    } catch (err) {
      console.error('[backtest] Error:', err);
      toast({
        title: 'Backtest Failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  const resetTest = () => {
    setTestResult(null);
    setTrades([]);
  };

  const returnColor = testResult && testResult.totalReturn >= 0 ? 'text-green-500' : 'text-destructive';
  const returnPrefix = testResult && testResult.totalReturn >= 0 ? '+' : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg">Backtest: {strategy.name}</DialogTitle>
        </DialogHeader>

        {!testResult ? (
          <div className="space-y-4">
            {/* Row 1: Symbol + Balance */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Symbol</Label>
                <Select
                  value={testParams.symbol}
                  onValueChange={(v) => setTestParams((p) => ({ ...p, symbol: v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SYMBOLS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Initial Balance ($)</Label>
                <Input
                  type="number"
                  className="h-9"
                  value={testParams.initialBalance}
                  onChange={(e) =>
                    setTestParams((p) => ({ ...p, initialBalance: Number(e.target.value) }))
                  }
                />
              </div>
            </div>

            {/* Row 2: Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  className="h-9"
                  value={testParams.startDate}
                  onChange={(e) => setTestParams((p) => ({ ...p, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  className="h-9"
                  value={testParams.endDate}
                  onChange={(e) => setTestParams((p) => ({ ...p, endDate: e.target.value }))}
                />
              </div>
            </div>

            {/* Strategy info compact row */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground py-1">
              <span>
                Timeframe: <span className="text-foreground font-medium">{strategy.timeframe}</span>
              </span>
              <span className="text-border">|</span>
              <span>
                Risk: <span className="text-foreground font-medium">{strategy.risk_level}</span>
              </span>
              <span className="text-border">|</span>
              <span>
                SL: <span className="text-foreground font-medium">{sl}%</span>
              </span>
              <span className="text-border">|</span>
              <span>
                TP: <span className="text-foreground font-medium">{tp}%</span>
              </span>
            </div>

            <p className="text-[11px] italic text-muted-foreground">
              Past performance does not guarantee future results.
            </p>

            <Button onClick={handleRunTest} disabled={testing} className="w-full">
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run Backtest
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 4 stat boxes */}
            <div className="grid grid-cols-4 gap-2">
              <div className="rounded-lg border border-border/20 bg-card/40 p-3 text-center">
                <div className={`text-lg font-bold ${returnColor}`}>
                  {returnPrefix}{testResult.totalReturn.toFixed(1)}%
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">Total Return</p>
              </div>
              <div className="rounded-lg border border-border/20 bg-card/40 p-3 text-center">
                <div className="text-lg font-bold text-foreground">
                  {testResult.winRate.toFixed(1)}%
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">Win Rate</p>
              </div>
              <div className="rounded-lg border border-border/20 bg-card/40 p-3 text-center">
                <div className="text-lg font-bold text-foreground">{testResult.totalTrades}</div>
                <p className="text-[10px] text-muted-foreground mt-0.5">Total Trades</p>
              </div>
              <div className="rounded-lg border border-border/20 bg-card/40 p-3 text-center">
                <div className="text-lg font-bold text-destructive">
                  -{testResult.maxDrawdown.toFixed(1)}%
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">Max Drawdown</p>
              </div>
            </div>

            {/* Equity curve */}
            <EquityCurve trades={trades} startBalance={testResult.startBalance} />

            {/* Detailed stats */}
            <div className="rounded-lg border border-border/20 bg-card/30 p-4 space-y-2 text-sm">
              <div className="flex justify-between py-1 border-b border-border/10">
                <span className="text-muted-foreground">Starting Balance</span>
                <span className="font-medium">${testResult.startBalance.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-border/10">
                <span className="text-muted-foreground">Ending Balance</span>
                <span className={`font-medium ${testResult.endBalance >= testResult.startBalance ? 'text-green-500' : 'text-destructive'}`}>
                  ${testResult.endBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-border/10">
                <span className="text-muted-foreground">Sharpe Ratio</span>
                <span className="font-medium">{testResult.sharpeRatio.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">Test Period</span>
                <span className="font-medium">
                  {testParams.startDate} → {testParams.endDate}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={resetTest}>
                <RotateCcw className="h-4 w-4" />
                Run New Test
              </Button>
              <Button className="flex-1">
                <Rocket className="h-4 w-4" />
                Deploy Strategy
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
